
import { useEffect, useRef, useState } from 'react';
import { Circuit, Day, SelectOption } from '../../lib/types/programEdit';
import { Box, Flex, FormControl, FormLabel, Tag, TagCloseButton, TagLabel, Input } from '@chakra-ui/react';
import StrongInput from '../common/StrongInput';
import StrongTextarea from '../common/StrongTextarea';
import StrongImageDragDrop from '../common/StrongImageDragDrop';

const DAY_ORDERS: SelectOption[] = [
  { label: 'Day 1', value: '1' },
  { label: 'Day 2', value: '2' },
  { label: 'Day 3', value: '3' },
  { label: 'Day 4', value: '4' },
  { label: 'Day 5', value: '5' },
  { label: 'Day 6', value: '6' },
  { label: 'Day 7', value: '7' },
];

type DayDetailProps = {
  weekIndex: number;
  dayIndex: number;
  day: Day;
  updateDay: (weekIndex: number, dayIndex: number, updatedDay: Day, isTypeIdUpdate?: boolean, typeId?: number) => void;
  onThumbnailFile?: (weekIndex: number, dayIndex: number, file: File | null) => void;
  warmupTemplates: { _id: string; title: string }[];
  selectedWarmups: Circuit[];
  addWarmupFromTemplate: (templateId: string, templateTitle: string) => void;
  removeWarmup: (templateIdOrTitle: string) => void;
};

const WarmupPillSelector = ({
  warmupTemplates,
  selectedWarmups,
  onAdd,
  onRemove,
}: {
  warmupTemplates: { _id: string; title: string }[];
  selectedWarmups: Circuit[];
  onAdd: (id: string, title: string) => void;
  onRemove: (templateIdOrTitle: string) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = warmupTemplates.filter((t) =>
    t.title.toLowerCase().includes(search.toLowerCase()),
  );
  const warmupTitleById = new Map(
    warmupTemplates.map((template) => [template._id, template.title]),
  );

  // Group warmups by warmupTemplateId so template-based warmups show as one pill
  // even when individual circuits have different titles. Fall back to title for
  // manually-added warmups that have no templateId.
  const uniqueWarmups = Array.from(
    new Map(
      selectedWarmups
        .filter((w) => w.warmupTemplateId || w.title)
        .map((w) => {
          const key = w.warmupTemplateId || w.title!;
          const label = w.warmupTemplateId
            ? warmupTitleById.get(w.warmupTemplateId) || w.warmupTemplateTitle || key
            : w.title || key;
          return [key, { key, label }];
        }),
    ).values(),
  );

  return (
    <Box position="relative" ref={containerRef}>
      <Box
        border="1px solid"
        borderColor={isOpen ? 'primary.400' : 'gray.200'}
        borderRadius="md"
        bg="white"
        p={2}
        minH="42px"
        cursor="text"
        onClick={() => setIsOpen(true)}
        transition="border-color 0.2s"
      >
        <Flex flexWrap="wrap" gap={2} align="center">
          {uniqueWarmups.map((warmup) => (
            <Tag key={warmup.key} colorScheme="primary" size="md" borderRadius="full">
              <TagLabel>{warmup.label}</TagLabel>
              <TagCloseButton
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(warmup.key);
                }}
              />
            </Tag>
          ))}
          <Input
            placeholder={selectedWarmups.length === 0 ? '+ Add Warmup' : ''}
            size="sm"
            variant="unstyled"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            minW="120px"
            flex="1"
          />
        </Flex>
      </Box>

      {isOpen && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          right={0}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="md"
          zIndex={20}
          maxH="220px"
          overflowY="auto"
        >
          {filtered.length === 0 ? (
            <Box px={3} py={2} color="gray.400" fontSize="sm">
              No warmups found
            </Box>
          ) : (
            filtered.map((template) => (
              <Box
                key={template._id}
                px={3}
                py={2}
                fontSize="sm"
                cursor="pointer"
                _hover={{ bg: 'primary.50' }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onAdd(template._id, template.title);
                  setSearch('');
                  setIsOpen(false);
                }}
              >
                {template.title}
              </Box>
            ))
          )}
        </Box>
      )}
    </Box>
  );
};

export const DayDetail = ({
  weekIndex,
  dayIndex,
  day,
  updateDay,
  onThumbnailFile,
  warmupTemplates,
  selectedWarmups,
  addWarmupFromTemplate,
  removeWarmup,
}: DayDetailProps) => {
  const [thumbnailPreview, setThumbnailPreview] = useState<string>(
    typeof day.thumbnail === 'string' ? day.thumbnail : '',
  );

  useEffect(() => {
    setThumbnailPreview(typeof day.thumbnail === 'string' ? day.thumbnail : '');
  }, [day.thumbnail]);

  const updateDayDetail = (key: string, value: any) => {
    const updatedDay = { ...day, [key]: value };
    updateDay(weekIndex, dayIndex, updatedDay);
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    updateDayDetail(name as keyof Day, value);
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    updateDayDetail(name as keyof Day, value);
  };

  const handleThumbnailSelect = (file: File | null) => {
    const nextThumbnail = file ? URL.createObjectURL(file) : '';
    setThumbnailPreview(nextThumbnail);
    if (!file) {
      updateDayDetail('thumbnail', '');
    }
    if (onThumbnailFile) onThumbnailFile(weekIndex, dayIndex, file);
  };

  return (
    <Flex justify="space-between" mb={4} mt="25px" align="stretch" minH="150px">
      <Box w="49%" display="flex" flexDirection="column">
        <FormControl flex="1" display="flex" flexDirection="column">
          <FormLabel>Description</FormLabel>
          <StrongTextarea
            name="description"
            value={day.description}
            onChange={handleChange}
            rows={6}
            style={{ flex: 1, resize: 'none' }}
          />
        </FormControl>
      </Box>
      <Box w="49%" display="flex" flexDirection="column" justifyContent="space-between">
        <FormControl mb={3}>
          <FormLabel color="gray.600">Thumbnail</FormLabel>
          <StrongImageDragDrop
            onFileSelect={handleThumbnailSelect}
            defaultImageUrl={thumbnailPreview}
            height="35%"
          />
        </FormControl>
        <FormControl mb={3}>
          <FormLabel>Vimeo Id</FormLabel>
          <StrongInput
            name="vimeoId"
            placeholder="Vimeo Id"
            value={day.vimeoId}
            onChange={handleTextChange}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Warmups</FormLabel>
          <WarmupPillSelector
            warmupTemplates={warmupTemplates}
            selectedWarmups={selectedWarmups}
            onAdd={addWarmupFromTemplate}
            onRemove={removeWarmup}
          />
        </FormControl>
      </Box>
    </Flex>
  );
};
